import { StandaloneSearchBox } from '@react-google-maps/api';
import React, { useRef, useState, useEffect } from "react";
import "./Autocomplete.css";
import styled from "styled-components";
import { Alert } from "@material-ui/lab";
import { useGoogleMaps } from "pages/Tracking/GoogleMapsManager";

function useClientRect() {
  const [rect, setRect] = useState(null);
  const ref = React.useCallback(node => {
    if (node !== null) {
      setRect(node);
    }
  }, []);
  return [rect, ref];
}

export default function Autocomplete({ onChange, value, name, readOnly, label }) {
  const { status, loadGoogleMaps } = useGoogleMaps();
  const autocomplete = useRef();
  const [validLocation, setValidLocation] = useState(false);
  const [location, setLocation] = useState(null);
  const [formatted, setFormatted] = React.useState(value);
  
  React.useEffect(() => {
    setFormatted(value);
  }, [value]);
  
  const onLoad = (a) => {
    autocomplete.current = a;
  };
  
  const getLocation = (location, components, nombre) => {
    const direccion = components.reduce((acc, component) => {
      if (component.types.includes("administrative_area_level_1")) {
        return { ...acc, estado: component.long_name };
      }
      if (component.types.includes("country")) {
        return { ...acc, pais: component.long_name };
      }
      if (component.types.includes("postal_code")) {
        return { ...acc, cp: component.long_name };
      }
      if (component.types.includes("route")) {
        return { ...acc, calle: component.long_name };
      }
      if (component.types.includes("street_number")) {
        return { ...acc, numero: component.long_name };
      }
      if (component.types.includes("sublocality_level_1")) {
        return { ...acc, colonia: component.long_name };
      }
      if (component.types.includes("locality")) {
        return { ...acc, ciudad: component.long_name };
      }
      return acc;
    }, {});
    
    return { ...location, direccion: { ...direccion, nombre_lugar: nombre } };
  };

  useEffect(() => {
    setValidLocation(location);
  }, [location]);

  useEffect(() => {
    setChanging(false);
    if (validLocation) onChange(location, name);
  }, [validLocation]);

  const onPlaceChanged = () => {
    if (autocomplete.current !== null) {
      const places = autocomplete.current.getPlaces();
      if (places && places.length > 0) {
        const place = places[0];
        const direccion = getLocation({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        }, place.address_components, place.name);
        setLocation({ ...direccion, formatted: place.formatted_address });
      }
    } else {
      console.log("Autocomplete is not loaded yet!");
    }
  };
  
  const [input, inputRef] = useClientRect();
  
  useEffect(() => {
    if (window.MutationObserver && input) {
      var observerHack = new MutationObserver(function() {
        observerHack.disconnect();
        input.autocomplete = 'disable-autofill';
      });
      observerHack.observe(input, {
        attributes: true,
        attributeFilter: ['autocomplete']
      });
    }
  }, [input]);
  
  const [changing, setChanging] = React.useState(false);
  const [currentSearch, setCurrentSearch] = React.useState("");
  const [blured, setBlured] = React.useState(false);

  if (status === "loading" || status === "idle") {
    return <div className="bg-blue-light rounded-3xl px-5 py-2 w-full">Cargando servicio de mapas...</div>;
  }

  if (status === "error") {
    return (
      <div className="w-full">
        <Alert severity="error">
          Error cargando Google Maps
          <button 
            onClick={loadGoogleMaps} 
            className="ml-4 px-2 py-1 bg-blue-500 text-white rounded"
          >
            Reintentar
          </button>
        </Alert>
      </div>
    );
  }
  
  // Safety check: ensure Google Maps API is really available
  if (!window.google || !window.google.maps || !window.google.maps.places) {
    console.error("Google Maps API reported as loaded but objects not available");
    return (
      <div className="w-full">
        <Alert severity="warning">
          Problema al inicializar Google Maps
          <button 
            onClick={loadGoogleMaps} 
            className="ml-4 px-2 py-1 bg-blue-500 text-white rounded"
          >
            Reintentar
          </button>
        </Alert>
      </div>
    );
  }

  const options = {
    language: 'es',
		// keyword:"port",
		types: ['establishment'],
		type: "airport"
  };

  return (
    <div className="w-full">
      {(changing || !value?.formatted) ? (
        <>
          <StandaloneSearchBox
            className="w-full"
            onLoad={onLoad}
            options={options}
            google={{ maps: { language: "es" } }}
            onPlacesChanged={onPlaceChanged}
          >
            <input
              className="bg-blue-light rounded-3xl px-5 py-2 w-full"
              ref={inputRef}
              autoComplete="one-time-code"
              onFocus={() => {
                try {
                  this.removeAttribute('readonly');
                } catch (e) {}
              }}
              type="text"
              placeholder={label}
              onChange={(e) => setCurrentSearch(e.target.value)}
              onBlur={() => setBlured(true)}
              onFocus={() => setBlured(false)}
              name="skdjnksd"
            />
          </StandaloneSearchBox>
          {blured && currentSearch.length > 0 && (
            <div className="text-[red] text-xs">
              Por favor selecciona una dirección de la lista
            </div>
          )}
        </>
      ) : (
        <div className="relative w-full">
          <input
            className="bg-blue-light rounded-3xl px-5 py-2 w-full"
            ref={inputRef}
            autoComplete="one-time-code"
            onFocus={() => {
              try {
                this.removeAttribute('readonly');
              } catch (e) {}
            }}
            type="text"
            placeholder={label}
            value={value?.formatted}
            readOnly
          />
          {!readOnly && (
            <button
              onClick={() => setChanging(true)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded"
            >
              x
            </button>
          )}
        </div>
      )}
      {location && !validLocation && (
        <Alert severity="error">Por favor ingresa una dirección valida</Alert>
      )}
    </div>
  );
}

const Input = styled.input`
  box-sizing: border-box;
  border: 1px solid transparent;
  width: 100%;
  border-color: var(--fieldSetColorPrimary);
  line-height: 33px;
  border-radius: 4px;
  padding-left: 10px;
`;